import { useEffect, useState } from 'react';
import { planById } from '@mybooks/plans';
import { AUTH_MODE, useSession } from './session';
import { api, unwrap } from '../api/client';
import { errorText } from '../ui/kit';
import { Logo } from '../ui/Logo';
import { isPlanLimit, PlanLimitNotice, PlanPicker } from '../pages/billing/Subscription';

const LOGO = <Logo size={44} />;

function Frame({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <div className="auth-page">
      <div className="card auth-card">
        {LOGO}
        <h1>{title}</h1>
        <p className="muted">{sub}</p>
        {children}
      </div>
    </div>
  );
}

/** Who paid in a checkout, so someone who bought on the website starts on "create your login". */
function useCheckoutDetails(checkout: string | null) {
  const [details, setDetails] = useState<{ email: string | null; plan: string | null } | null>(null);
  useEffect(() => {
    if (!checkout) return;
    unwrap(api.GET('/v1/billing/checkout-sessions/{id}', { params: { path: { id: checkout } } }))
      .then((r) => setDetails({ email: r.data?.email ?? null, plan: r.data?.plan ?? null }), () => setDetails(null));
  }, [checkout]);
  return details;
}

/** Email and password sign-in through Supabase, with account creation. */
export function SignIn({ invited = false, checkout = null }: { invited?: boolean; checkout?: string | null }) {
  const session = useSession();
  const [mode, setMode] = useState<'in' | 'up'>(checkout ? 'up' : 'in');
  const [email, setEmail] = useState('');
  const paid = useCheckoutDetails(checkout);
  useEffect(() => { if (paid?.email) setEmail((e) => e || paid.email || ''); }, [paid]);
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (AUTH_MODE === 'unconfigured') {
    return <Frame title="MyBooks" sub="Sign-in is not configured for this build. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY and rebuild."><span /></Frame>;
  }
  if (sent) return <Frame title="Check your email" sub={`We sent a confirmation link to ${email}. Open it, then sign in here.${invited ? ' The link brings you back to your invitation.' : checkout ? ' Your plan is added to your account when you do.' : ''}`}><button className="btn primary" onClick={() => { setSent(false); setMode('in'); }}>Back to sign in</button></Frame>;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Enter an email like name@example.com'); return; }
    if (password.length < (mode === 'up' ? 8 : 1)) { setError(mode === 'up' ? 'Use at least 8 characters' : 'Enter your password'); return; }
    setBusy(true);
    try {
      if (mode === 'in') await session.signIn(email, password);
      else if ((await session.signUp(email, password, name.trim())) === 'confirm-email') setSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const plan = planById(paid?.plan ?? '');
  const sub = invited
    ? `${mode === 'in' ? 'Sign in' : 'Create your account'} with the email address your invitation was sent to, and you join the business straight after.`
    : checkout
      ? `Thank you for subscribing${plan ? ` to ${plan.name}` : ''}. ${mode === 'up' ? 'Create your login' : 'Sign in'} and your plan is added to your account.`
      : mode === 'in' ? 'Bookkeeping for small businesses' : 'Then set up your first business';
  return (
    <Frame title={checkout && mode === 'up' ? 'Create your login' : mode === 'in' ? 'Sign in to MyBooks' : 'Create your account'} sub={sub}>
      <form className="auth-form" onSubmit={submit} noValidate>
        {mode === 'up' && <div className="field"><label htmlFor="su-name">Your name</label><input className="input" id="su-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} /></div>}
        <div className="field"><label htmlFor="si-email">Email</label><input className="input" id="si-email" type="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="field"><label htmlFor="si-password">Password</label><input className="input" id="si-password" type="password" autoComplete={mode === 'in' ? 'current-password' : 'new-password'} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
        {error && <div className="error" role="alert">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy}>{busy ? 'Please wait…' : mode === 'in' ? 'Sign in' : 'Create account'}</button>
      </form>
      <button className="link-btn" onClick={() => { setMode(mode === 'in' ? 'up' : 'in'); setError(''); }}>{mode === 'in' ? 'New to MyBooks? Create an account' : 'Already have an account? Sign in'}</button>
    </Frame>
  );
}

/** Shown when the signed-in person has no business yet, or (in development) to pick one. */
export function FirstBusiness() {
  const session = useSession();
  const [name, setName] = useState('');
  const [devId, setDevId] = useState('');
  const [error, setError] = useState('');
  const [overLimit, setOverLimit] = useState(false);
  const [busy, setBusy] = useState(false);
  const failed = (err: unknown) => { setOverLimit(isPlanLimit(err)); setError(errorText(err)); setBusy(false); };
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Enter the business name'); return; }
    setBusy(true);
    try { await session.createBusiness(name.trim(), 'USD'); } catch (err) { failed(err); }
  };
  return (
    <Frame title="Set up your business" sub={AUTH_MODE === 'dev' ? 'Development sign-in: this talks to wrangler dev with the dev-only headers.' : 'You will be its owner. MyBooks sets up the standard US chart of accounts.'}>
      <form className="auth-form" onSubmit={create} noValidate>
        <div className="field"><label htmlFor="fb-name">Business name</label><input className="input" id="fb-name" autoFocus value={name} onChange={(e) => { setName(e.target.value); setError(''); setOverLimit(false); }} /></div>
        {error && (overLimit ? <PlanLimitNotice message={error} inShell={false} /> : <div className="error" role="alert">{error}</div>)}
        <button className="btn primary" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create business'}</button>
      </form>
      {overLimit && <PlanPicker />}
      {AUTH_MODE === 'dev' && (
        <form className="auth-form" onSubmit={(e) => { e.preventDefault(); if (/^[0-9a-f-]{36}$/i.test(devId.trim())) session.addDevTenant(devId.trim(), 'Existing business'); }}>
          <div className="field"><label htmlFor="fb-id">Or use an existing tenant id</label><input className="input" id="fb-id" value={devId} onChange={(e) => setDevId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" /></div>
          <button className="btn" type="submit">Use this business</button>
        </form>
      )}
      {session.archived.length > 0 && (
        <div className="auth-form">
          <span className="muted">Archived businesses you own</span>
          {session.archived.map((a) => (
            <button key={a.id} type="button" className="btn" disabled={busy} onClick={() => { setBusy(true); session.restoreBusiness(a.id).catch(failed); }}>Restore {a.name}</button>
          ))}
        </div>
      )}
      {AUTH_MODE === 'supabase' && <button className="link-btn" onClick={() => { void session.signOut(); }}>Sign out</button>}
    </Frame>
  );
}
